import React, { useEffect, useState } from "react";
import { Quote, Plus, Trash2, Save, Eye, EyeOff, X, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import LoadErrorBanner, { describeLoadError } from "@/components/admin/LoadErrorBanner";

const PAGES = [{ id: "home", label: "Homepage" }, { id: "for-hotels", label: "/for-hotels" }, { id: "get-quotes", label: "/get-quotes" }];
const PREVIEW_CONTEXT = {
  home: { where: "Homepage · under “How it works”", bg: "bg-white", eyebrow: "For hoteliers" },
  "for-hotels": { where: "/for-hotels · above “Buyer updates”", bg: "bg-white", eyebrow: "" },
  "get-quotes": { where: "/get-quotes · below “Trusted by suppliers including”", bg: "bg-[#F9F8F6]", eyebrow: "" },
};

// Mirrors RotatingTestimonial's markup so the editor preview matches the live page exactly.
function PagePreview({ page, quote, attribution }) {
  const ctx = PREVIEW_CONTEXT[page];
  return (
    <div className={`${ctx.bg} border border-[#E8ECE9] p-4`} data-testid={`testimonial-preview-${page}`}>
      <div className="text-[9px] uppercase tracking-[0.2em] text-[#7D8C85] mb-3">{ctx.where}</div>
      {ctx.eyebrow && <div className="tracking-overline text-[10px] mb-2 opacity-60">{ctx.eyebrow}</div>}
      <figure className="max-w-3xl border-l-4 border-[#264839] pl-4 py-1">
        <blockquote className="font-serif text-lg leading-snug text-[#11211A]" data-testid={`testimonial-preview-quote-${page}`}>“{quote.trim() || "Your quote will appear here as you type."}”</blockquote>
        <figcaption className="mt-3 text-[11px] uppercase tracking-[0.2em] text-[#7D8C85]">{attribution.trim() || "Role, State"}</figcaption>
      </figure>
    </div>
  );
}
const EMPTY = { quote: "", attribution: "", active: true, pages: ["home"] };

// Admin → Testimonials: add / edit / hide / delete the anonymous quotes that rotate on the homepage. Live immediately.
export default function TestimonialsManager() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  const onDrop = async (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); setOverId(null); return; }
    const ids = items.map((t) => t.id);
    const from = ids.indexOf(dragId); const to = ids.indexOf(targetId);
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    const byId = Object.fromEntries(items.map((t) => [t.id, t]));
    setItems(ids.map((id) => byId[id])); setDragId(null); setOverId(null);
    try { await api.put("/admin/testimonials/reorder", { ids }); toast.success("Order saved"); }
    catch { toast.error("Could not save order"); load(); }
  };

  const load = async () => {
    setError(null);
    try { const { data } = await api.get("/admin/testimonials"); setItems(data.items || []); }
    catch (e) { setError(describeLoadError(e, "testimonials")); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm(EMPTY); };
  const startEdit = (t) => { setEditing(t.id); setForm({ quote: t.quote, attribution: t.attribution, active: t.active !== false, pages: t.pages?.length ? t.pages : ["home"] }); };
  const togglePage = (id) => setForm((f) => ({ ...f, pages: f.pages.includes(id) ? f.pages.filter((p) => p !== id) : [...f.pages, id] }));
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing === "new") await api.post("/admin/testimonials", form);
      else await api.put(`/admin/testimonials/${editing}`, form);
      toast.success(editing === "new" ? "Testimonial added — live on the homepage now" : "Testimonial updated");
      cancel(); load();
    } catch (err) { toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  const toggle = async (t) => {
    try { await api.put(`/admin/testimonials/${t.id}`, { quote: t.quote, attribution: t.attribution, active: t.active === false, pages: t.pages?.length ? t.pages : ["home"] }); load(); }
    catch { toast.error("Could not update"); }
  };
  const remove = async (t) => {
    if (!window.confirm("Remove this testimonial from the rotation permanently?")) return;
    try { await api.delete(`/admin/testimonials/${t.id}`); toast.success("Removed"); load(); }
    catch { toast.error("Could not remove"); }
  };

  const activeCount = items.filter((t) => t.active !== false).length;
  return (
    <div className="bg-white border border-[#E8ECE9] p-5" data-testid="testimonials-manager">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="font-serif text-lg text-[#11211A] flex items-center gap-2"><Quote className="w-4 h-4 text-[#264839]" /> Homepage testimonials</div>
          <p className="text-xs text-[#7D8C85] mt-0.5">Anonymous buyer quotes that rotate under "How it works" — one per visit. Drag the handle to set rotation order. Changes are live immediately, no redeploy. <span data-testid="testimonials-active-count">{activeCount} active</span>.</p>
        </div>
        <button type="button" onClick={startNew} className="btn-primary text-xs" data-testid="testimonial-add"><Plus className="w-3.5 h-3.5" /> Add quote</button>
      </div>
      <LoadErrorBanner error={error} onRetry={load} testId="testimonials-error" />
      {editing && (
        <form onSubmit={save} className="mb-4 bg-[#F7F6F2] border border-[#E8ECE9] p-4 space-y-3" data-testid="testimonial-form">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-[#7D8C85] mb-1">Quote (no quotation marks needed)</label>
            <textarea required minLength={10} maxLength={400} rows={3} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} className="w-full border border-[#C4CFC9] px-3 py-2 text-sm rounded-sm" data-testid="testimonial-quote-input" />
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[240px]">
              <label className="block text-[10px] uppercase tracking-widest text-[#7D8C85] mb-1">Attribution (role, state — no names or properties)</label>
              <input required minLength={2} maxLength={120} value={form.attribution} onChange={(e) => setForm({ ...form, attribution: e.target.value })} placeholder="e.g. Procurement Lead, New South Wales" className="w-full border border-[#C4CFC9] px-3 py-2 text-sm rounded-sm" data-testid="testimonial-attribution-input" />
            </div>
            <div className="pb-2" data-testid="testimonial-pages">
              <div className="text-[10px] uppercase tracking-widest text-[#7D8C85] mb-1">Show on</div>
              <div className="flex flex-wrap gap-3">
                {PAGES.map((p) => (
                  <label key={p.id} className="flex items-center gap-1.5 text-xs text-[#4A5550]"><input type="checkbox" checked={form.pages.includes(p.id)} onChange={() => togglePage(p.id)} data-testid={`testimonial-page-${p.id}`} /> {p.label}</label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs text-[#4A5550] pb-2"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="testimonial-active-input" /> In rotation</label>
            <button type="submit" disabled={busy || form.pages.length === 0} className="btn-primary text-xs disabled:opacity-60" data-testid="testimonial-save"><Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save"}</button>
            <button type="button" onClick={cancel} className="btn-ghost text-xs" data-testid="testimonial-cancel"><X className="w-3.5 h-3.5" /> Cancel</button>
          </div>
          <div className="pt-3 border-t border-[#E8ECE9]" data-testid="testimonial-live-preview">
            <div className="text-[10px] uppercase tracking-widest text-[#7D8C85] mb-2">Live preview {form.pages.length ? `· ${form.pages.length} page${form.pages.length === 1 ? "" : "s"}` : "· select at least one page"}</div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {form.pages.map((pg) => <PagePreview key={pg} page={pg} quote={form.quote} attribution={form.attribution} />)}
            </div>
          </div>
        </form>
      )}
      <ul className="divide-y divide-[#F0EFEA]">
        {items.map((t) => (
          <li key={t.id} draggable onDragStart={() => setDragId(t.id)} onDragOver={(e) => { e.preventDefault(); setOverId(t.id); }} onDragLeave={() => setOverId(null)} onDrop={() => onDrop(t.id)} onDragEnd={() => { setDragId(null); setOverId(null); }}
              className={`py-3 flex items-start gap-3 transition-colors ${t.active === false ? "opacity-50" : ""} ${overId === t.id && dragId !== t.id ? "bg-[#F0EFEA] border-t-2 border-[#264839]" : ""} ${dragId === t.id ? "opacity-40" : ""}`} data-testid={`testimonial-row-${t.id}`} data-order={t.order}>
            <button type="button" className="cursor-grab active:cursor-grabbing text-[#C4CFC9] hover:text-[#264839] mt-1" title="Drag to reorder" aria-label="Drag to reorder" data-testid={`testimonial-drag-${t.id}`}><GripVertical className="w-4 h-4" /></button>
            <div className="flex-1 min-w-0">
              <div className="font-serif text-base text-[#11211A] leading-snug" data-testid={`testimonial-quote-${t.id}`}>“{t.quote}”</div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#7D8C85] mt-1" data-testid={`testimonial-attribution-${t.id}`}>{t.attribution}{t.active === false && " · hidden"}</div>
              <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`testimonial-pages-${t.id}`}>
                {PAGES.map((p) => <span key={p.id} className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 ${(t.pages?.length ? t.pages : ["home"]).includes(p.id) ? "bg-[#264839] text-white" : "bg-[#F0EFEA] text-[#9AA6A0]"}`}>{p.label}</span>)}
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button type="button" onClick={() => startEdit(t)} className="btn-ghost text-xs" data-testid={`testimonial-edit-${t.id}`}>Edit</button>
              <button type="button" onClick={() => toggle(t)} className="btn-ghost text-xs" title={t.active === false ? "Show in rotation" : "Hide from rotation"} data-testid={`testimonial-toggle-${t.id}`}>{t.active === false ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}</button>
              <button type="button" onClick={() => remove(t)} className="btn-ghost text-xs text-[#B35C4A]" data-testid={`testimonial-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </li>
        ))}
        {items.length === 0 && !error && <li className="py-6 text-xs text-[#7D8C85] text-center" data-testid="testimonials-empty">No testimonials yet — add one and it appears on the homepage immediately.</li>}
      </ul>
    </div>
  );
}
